import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";

/**
 * M26a — one source for the account's currency.
 *
 * Before this, `formatMoney`/`MoneyDelta` defaulted to "USD" and only the Dashboard threaded
 * the real currency through, so a EUR account showed "$" everywhere else. The provider is
 * mounted once in the shell and fetched once per session (`staleTime: Infinity` — an account
 * does not change denomination mid-session, and if it ever did, a reload is the right
 * response to something that drastic).
 *
 * The context default is "USD", not "": two different failures collapse into it on purpose.
 * A component rendered outside the provider (every existing test mounts `MoneyDelta` bare)
 * and a server that has never taken an account snapshot (answers "") both fall back to the
 * pre-M26a behaviour, because `Intl.NumberFormat` throws a RangeError on an empty currency
 * code — a whole screen lost to a display concern.
 */
const CurrencyContext = createContext<string>("USD");

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: queryKeys.currency,
    queryFn: api.currency,
    staleTime: Infinity,
  });

  const currency = query.data?.currency || "USD";
  return <CurrencyContext.Provider value={currency}>{children}</CurrencyContext.Provider>;
}

/** The account's ISO currency code, or "USD" when unknown. Never "". */
export function useCurrency(): string {
  return useContext(CurrencyContext);
}
